import { type FC, type MouseEventHandler, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { trackEvent } from 'dibs-tracking';
import classnames from 'classnames';
import { useHomePageContext } from '../HomePageContext';
import { useBuyerIncentivesList } from 'dibs-buyer-incentives/exports/useBuyerIncentivesList';
import BuyerIncentiveIcon from 'dibs-buyer-incentives/exports/BuyerIncentiveIcon';
import BuyerIncentiveCopy from 'dibs-buyer-incentives/exports/BuyerIncentiveCopy';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import {
    useIsRemoveShopByTestVariant,
    trackRemoveShopByTest,
} from '../helpers/removeShopByTestHelpers';

import dibsCss from 'dibs-css';
import styles from './HpSharedSkinnyBannerModule.module.css';

const HpSharedSkinnyBannerModule: FC = () => {
    const { isMobile } = useHomePageContext();
    const intl = useIntl();
    const buyerIncentivesList = useBuyerIncentivesList({ useAll: true });
    const isRemoveShopByTestVariant = useIsRemoveShopByTestVariant();

    const handleClick: MouseEventHandler = useCallback(event => {
        trackEvent(
            {
                category: 'promo interaction',
                action: 'Learn More Click',
                label: '[homepage]',
            },
            event
        );
    }, []);

    const learnMoreLink = (
        <SeoLink
            className={classnames(dibsCss.sassyFontBody, dibsCss.inlineBlock, dibsCss.textNoir)}
            dataTn="skinny-banner-link"
            linkData={{
                path: handleLocaleUrl('/about/promise/', intl.locale),
                isLinkable: true,
            }}
            onClick={handleClick}
            target="_blank"
        >
            <FormattedMessage
                id="HpSharedSkinnyBannerModule.learnMore"
                defaultMessage="Learn More"
            />
        </SeoLink>
    );

    return (
        <div
            className={classnames(dibsCss.bgShagreen200, dibsCss.textNoir, {
                [dibsCss.mtLarger]: !isMobile,
                [dibsCss.pxLarger]: !isMobile,
                [dibsCss.pyXxlarge]: !isMobile,
                [dibsCss.flex]: !isMobile,
                [dibsCss.justifyBetween]: !isMobile,
                [dibsCss.itemsCenter]: !isMobile,
                [dibsCss.gapMedium]: !isMobile,

                [dibsCss.mtLarge]: isMobile,
                [dibsCss.pyLarge]: isMobile,
                [dibsCss.pxSmall]: isMobile,
                [dibsCss.textCenter]: isMobile,
                [dibsCss.mbLarge]: isRemoveShopByTestVariant && isMobile,
                [dibsCss.mbLarger]: isRemoveShopByTestVariant && !isMobile,
            })}
        >
            <div
                className={classnames({
                    [styles.header]: !isMobile,
                })}
            >
                <div
                    className={classnames(dibsCss.mbXsmall, {
                        [dibsCss.textHeaderBannerSmallNormal]: !isMobile,
                        [dibsCss.sassyFontHeaderSizeLarge]: isMobile,
                    })}
                    data-tn="skinny-banner-title"
                >
                    <FormattedMessage
                        id="HpSharedSkinnyBannerModule.title"
                        defaultMessage="The 1stDibs <i>Promise</i>"
                        values={{
                            i: chunks =>
                                isMobile ? (
                                    chunks
                                ) : (
                                    <span className={dibsCss.textHeaderBannerSmallItalic}>
                                        {chunks}
                                    </span>
                                ),
                        }}
                    />
                </div>
                <div
                    className={classnames(dibsCss.sassyFontHeaderSizeMedium, {
                        [dibsCss.sassyFontHeaderSizeMedium]: !isMobile,
                        [dibsCss.mbXsmall]: !isMobile,
                        [dibsCss.sassyFontHeaderSizeSmall]: isMobile,
                        [dibsCss.mbSmall]: isMobile,
                    })}
                >
                    <FormattedMessage
                        id="HpSharedSkinnyBannerModule.subTitle"
                        defaultMessage="We’re committed to your satisfaction and peace of mind with every purchase."
                    />
                </div>
                {!isMobile && learnMoreLink}
            </div>

            <div
                className={classnames(dibsCss.grid, {
                    [styles.incentivesMultiColumn]: !isMobile,
                    [dibsCss.gapMedium]: !isMobile,
                    [styles.incentivesSingleColumn]: isMobile,
                    [dibsCss.inlineGrid]: isMobile,
                    [dibsCss.gapXsmall]: isMobile,
                    [dibsCss.mbSmall]: isMobile,
                })}
            >
                {buyerIncentivesList.map((name, index) => (
                    <div
                        key={index}
                        className={classnames(dibsCss.flex, {
                            [dibsCss.sassyFontBodyLarge]: !isMobile,
                            [dibsCss.gapXsmall]: !isMobile,
                            [dibsCss.flexCol]: !isMobile,

                            [dibsCss.sassyFontBody]: isMobile,
                            [dibsCss.gapSmaller]: isMobile,
                            [dibsCss.itemsCenter]: isMobile,
                        })}
                    >
                        <span className={dibsCss.fillShagreen800}>
                            <BuyerIncentiveIcon name={name} size={isMobile ? '20' : '24'} />
                        </span>

                        <BuyerIncentiveCopy name={name} />
                    </div>
                ))}
            </div>
            {isMobile && <div className={dibsCss.block}>{learnMoreLink}</div>}
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        // We want to track Remove Shop By test only when user scrolls down
                        // to the bottom of the banner where Shop By module supposed to be.
                        trackRemoveShopByTest();
                    }
                }}
            />
        </div>
    );
};

export default HpSharedSkinnyBannerModule;
